/**
 * useColumnWidths Hook
 *
 * Manages user-adjusted column widths for ArTable.
 * Persists to localStorage for session continuity.
 */

import { useState, useCallback } from 'react';
import type { TypeId } from '#types/System.Artifacts.types';

type ColumnWidths = Record<TypeId, number>;

type UseColumnWidthsReturn = {
  userWidths: ColumnWidths;
  updateWidth: (typeId: TypeId, width: number) => void;
  resetWidth: (typeId: TypeId) => void;
  resetAllWidths: () => void;
};

const getStorageKey = (tableId: string): string => `arTable-columnWidths-${tableId}`;

const loadFromStorage = (tableId: string): ColumnWidths => {
  try {
    const stored = localStorage.getItem(getStorageKey(tableId));
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveToStorage = (tableId: string, widths: ColumnWidths): void => {
  try {
    if (Object.keys(widths).length === 0) {
      localStorage.removeItem(getStorageKey(tableId));
    } else {
      localStorage.setItem(getStorageKey(tableId), JSON.stringify(widths));
    }
  } catch {
    // Storage unavailable, ignore
  }
};

/**
 * Hook for managing user-adjusted column widths.
 * Persists widths to localStorage keyed by tableId.
 */
export const useColumnWidths = (tableId: string): UseColumnWidthsReturn => {
  const [userWidths, setUserWidths] = useState<ColumnWidths>(() => loadFromStorage(tableId));

  const updateWidth = useCallback(
    (typeId: TypeId, width: number) => {
      setUserWidths((prev) => {
        const next = { ...prev, [typeId]: width };
        saveToStorage(tableId, next);
        return next;
      });
    },
    [tableId]
  );

  const resetWidth = useCallback(
    (typeId: TypeId) => {
      setUserWidths((prev) => {
        const next = { ...prev };
        delete next[typeId];
        saveToStorage(tableId, next);
        return next;
      });
    },
    [tableId]
  );

  const resetAllWidths = useCallback(() => {
    setUserWidths({});
    saveToStorage(tableId, {});
  }, [tableId]);

  return { userWidths, updateWidth, resetWidth, resetAllWidths };
};

export default useColumnWidths;
